'use client';

import { useEffect, useState, useMemo } from 'react';
import { Section, SectionHeader } from '@/components/layout';
import TrainingCard from './TrainingCard';
import { splitTrainingContent } from '../utils/contentSplitter';
import { getTrainingStatus, isTraining5Unlocked, isTraining6Unlocked, isTraining7Unlocked, isTraining8Unlocked } from '../utils/progressTracker';

interface TrainingDashboardProps {
  fullContent: string;
  training3Content?: string;
  training4Content?: string;
  training5Content?: string;
  training6Content?: string;
  training7Content?: string;
  training8Content?: string;
  traineeId?: string;
}

export default function TrainingDashboard({ fullContent, training3Content = '', training4Content = '', training5Content = '', training6Content = '', training7Content = '', training8Content = '', traineeId = 'default' }: TrainingDashboardProps) {
  const [training1Sections, setTraining1Sections] = useState(0);
  const [training2Sections, setTraining2Sections] = useState(0);
  const [training3Sections, setTraining3Sections] = useState(0);
  const [training4Sections, setTraining4Sections] = useState(0);
  const [training5Sections, setTraining5Sections] = useState(0);
  const [training6Sections, setTraining6Sections] = useState(0);
  const [training7Sections, setTraining7Sections] = useState(0);
  const [training8Sections, setTraining8Sections] = useState(0);
  const [training1Status, setTraining1Status] = useState({
    trainingId: 'training-1',
    isLocked: false,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training2Status, setTraining2Status] = useState({
    trainingId: 'training-2',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training3Status, setTraining3Status] = useState({
    trainingId: 'training-3',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training4Status, setTraining4Status] = useState({
    trainingId: 'training-4',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training5Status, setTraining5Status] = useState({
    trainingId: 'training-5',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training6Status, setTraining6Status] = useState({
    trainingId: 'training-6',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training7Status, setTraining7Status] = useState({
    trainingId: 'training-7',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [training8Status, setTraining8Status] = useState({
    trainingId: 'training-8',
    isLocked: true,
    isComplete: false,
    progress: 0,
    completedSections: 0,
    totalSections: 0,
    lastUpdated: null as string | null
  });
  const [isClient, setIsClient] = useState(false);

  // Split content into Training 1 and Training 2 (Training 3 comes from separate content)
  const { training1, training2 } = useMemo(() => {
    return splitTrainingContent(fullContent);
  }, [fullContent]);

  // Extract training titles and descriptions from markdown
  // Supports both H1 (# Training 1) and H2 (## Training 1) formats
  // v2 file uses "What this training is about" instead of "Purpose"
  const training1Info = useMemo(() => {
    // Support both H1 and H2 for title
    const titleMatch = training1.match(/^#+\s+Training 1[^\n]*(.+)$/m);
    // Try both "Purpose" (v1) and "What this training is about" (v2)
    const purposeMatch = training1.match(/(?:### Purpose|## What this training is about)\s*\n([\s\S]*?)(?=\n##|$)/);
    
    const title = titleMatch 
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '') // Remove leading dash and trailing ")"
      : 'Training 1 – VibeCoding Core Workflow';
    
    const description = purposeMatch 
      ? purposeMatch[1].trim().split('\n')[0] // First line of description
      : 'Learn the VibeCoding workflow using Cursor and secondary AI tools.';

    return { title, description };
  }, [training1]);

  const training2Info = useMemo(() => {
    // Support both H1 and H2 for title
    const titleMatch = training2.match(/^#+\s+Training 2[^\n]*(.+)$/m);
    // Try both "Purpose" (v1) and "What this training is about" (v2)
    const purposeMatch = training2.match(/(?:### Purpose|## What this training is about)\s*\n([\s\S]*?)(?=\n##|$)/);
    
    const title = titleMatch 
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '') // Remove leading dash and trailing ")"
      : 'Training 2 – Figma Basics for VibeCoding';
    
    const description = purposeMatch 
      ? purposeMatch[1].trim().split('\n')[0] // First line of description
      : 'Learn to use Figma as the visual source of truth for UI changes.';

    return { title, description };
  }, [training2]);

  const training3Info = useMemo(() => {
    if (!training3Content.trim()) return { title: 'Training 3', description: '' };
    const titleMatch = training3Content.match(/^#+\s+Training 3[^\n]*(.+)$/m);
    const purposeMatch = training3Content.match(/## What this training is about\s*\n([\s\S]*?)(?=\n##|$)/);
    const title = titleMatch
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '')
      : 'Training 3 – Figma → Cursor UI Upgrade';
    const description = purposeMatch
      ? purposeMatch[1].trim().split('\n')[0]
      : 'Redesign a real UI component with Figma and Cursor.';
    return { title, description };
  }, [training3Content]);

  const training4Info = useMemo(() => {
    if (!training4Content.trim()) return { title: 'Training 4', description: '' };
    const titleMatch = training4Content.match(/^#+\s+Training 4[^\n]*(.+)$/m);
    const purposeMatch = training4Content.match(/## What this training is about\s*\n([\s\S]*?)(?=\n##|$)/);
    const title = titleMatch
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '')
      : 'Training 4 – Website QA + Design Improvements';
    const description = purposeMatch
      ? purposeMatch[1].trim().split('\n')[0]
      : 'Audit a website and implement two quality improvements.';
    return { title, description };
  }, [training4Content]);

  const training5Info = useMemo(() => {
    if (!training5Content.trim()) return { title: 'Training 5', description: '' };
    const titleMatch = training5Content.match(/^#+\s+Training 5[^\n]*(.+)$/m);
    const purposeMatch = training5Content.match(/## What this training is about\s*\n([\s\S]*?)(?=\n##|$)/);
    const title = titleMatch
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '')
      : 'Training 5 – Web App Change Workflow';
    const description = purposeMatch
      ? purposeMatch[1].trim().split('\n')[0]
      : 'Plan, implement, verify, and package changes to an existing web app.';
    return { title, description };
  }, [training5Content]);

  const training6Info = useMemo(() => {
    if (!training6Content.trim()) return { title: 'Training 6', description: '' };
    const titleMatch = training6Content.match(/^#+\s+Training 6[^\n]*(.+)$/m);
    const purposeMatch = training6Content.match(/## What this training is about\s*\n([\s\S]*?)(?=\n##|$)/);
    const title = titleMatch
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '')
      : 'Training 6 – New Project Kickoff with Documentation Pack';
    const description = purposeMatch
      ? purposeMatch[1].trim().split('\n')[0]
      : 'Training 6 – New Project Kickoff';
    return { title, description };
  }, [training6Content]);

  const training7Info = useMemo(() => {
    if (!training7Content.trim()) return { title: 'Training 7', description: '' };
    const titleMatch = training7Content.match(/^#+\s+Training 7[^\n]*(.+)$/m);
    const purposeMatch = training7Content.match(/## What this training is about\s*\n([\s\S]*?)(?=\n##|$)/);
    const title = titleMatch
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '')
      : 'Training 7 – StayEasy Page Improvement Challenge';
    const description = purposeMatch
      ? purposeMatch[1].trim().split('\n')[0]
      : 'Training 7 – Page Improvement Challenge';
    return { title, description };
  }, [training7Content]);

  const training8Info = useMemo(() => {
    if (!training8Content.trim()) return { title: 'Training 8', description: '' };
    const titleMatch = training8Content.match(/^#+\s+Training 8[^\n]*(.+)$/m);
    const purposeMatch = training8Content.match(/(?:## What this training is about|## What this task is about)\s*\n([\s\S]*?)(?=\n##|$)/);
    const title = titleMatch
      ? titleMatch[1].trim().replace(/^–\s*/, '').replace(/\s*\)\s*$/, '')
      : 'Training 8 – StayEasy Expedia-Grade UI Upgrade';
    const description = purposeMatch
      ? purposeMatch[1].trim().split('\n')[0]
      : 'Training 8 – Expedia-Grade UI Upgrade';
    return { title, description };
  }, [training8Content]);

  // Mark as client-side rendered to avoid hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Count sections (h2 headings that will have checkboxes) in each training
  useEffect(() => {
    const h2Regex = /^##\s+(.+)$/gm;
    const training1Matches = Array.from(training1.matchAll(h2Regex));
    setTraining1Sections(training1Matches.length);

    const training2Matches = Array.from(training2.matchAll(h2Regex));
    setTraining2Sections(training2Matches.length);

    if (training3Content.trim()) {
      const training3Matches = Array.from(training3Content.matchAll(h2Regex));
      setTraining3Sections(training3Matches.length);
    }
    if (training4Content.trim()) {
      const training4Matches = Array.from(training4Content.matchAll(h2Regex));
      setTraining4Sections(training4Matches.length);
    }
    if (training5Content.trim()) {
      const training5Matches = Array.from(training5Content.matchAll(h2Regex));
      setTraining5Sections(training5Matches.length);
    }
    if (training6Content.trim()) {
      const training6Matches = Array.from(training6Content.matchAll(h2Regex));
      setTraining6Sections(training6Matches.length);
    }
    if (training7Content.trim()) {
      const training7Matches = Array.from(training7Content.matchAll(h2Regex));
      setTraining7Sections(training7Matches.length);
    }
    if (training8Content.trim()) {
      const training8Matches = Array.from(training8Content.matchAll(h2Regex));
      setTraining8Sections(training8Matches.length);
    }
  }, [training1, training2, training3Content, training4Content, training5Content, training6Content, training7Content, training8Content]);

  const applyProgressFromApi = (items: Array<{ trainingId: string; completed: string[]; progress: number; markedComplete: boolean; lastUpdated: string | null }>) => {
    const p1 = items.find((p) => p.trainingId === 'training-1');
    const p2 = items.find((p) => p.trainingId === 'training-2');
    const p3 = items.find((p) => p.trainingId === 'training-3');
    const p4 = items.find((p) => p.trainingId === 'training-4');
    const p5 = items.find((p) => p.trainingId === 'training-5');
    const p6 = items.find((p) => p.trainingId === 'training-6');
    const p7 = items.find((p) => p.trainingId === 'training-7');
    const p8 = items.find((p) => p.trainingId === 'training-8');
    if (training1Sections > 0) {
      const completed = p1?.completed?.length ?? 0;
      const isComplete = (p1?.markedComplete === true) || completed >= training1Sections;
      setTraining1Status((s) => ({
        ...s,
        progress: p1?.progress ?? 0,
        completedSections: completed,
        totalSections: training1Sections,
        isComplete,
        lastUpdated: p1?.lastUpdated ?? null,
      }));
    }
    if (training2Sections > 0) {
      const training1Complete = (p1?.markedComplete === true) || (p1?.completed?.length ?? 0) >= training1Sections;
      const completed = p2?.completed?.length ?? 0;
      const isComplete = (p2?.markedComplete === true) || completed >= training2Sections;
      setTraining2Status((s) => ({
        ...s,
        isLocked: !training1Complete,
        progress: p2?.progress ?? 0,
        completedSections: completed,
        totalSections: training2Sections,
        isComplete,
        lastUpdated: p2?.lastUpdated ?? null,
      }));
    }
    if (training3Sections > 0) {
      const training2Complete = (p2?.markedComplete === true) || (p2?.completed?.length ?? 0) >= training2Sections;
      const completed = p3?.completed?.length ?? 0;
      const isComplete = (p3?.markedComplete === true) || completed >= training3Sections;
      setTraining3Status((s) => ({
        ...s,
        isLocked: !training2Complete,
        progress: p3?.progress ?? 0,
        completedSections: completed,
        totalSections: training3Sections,
        isComplete,
        lastUpdated: p3?.lastUpdated ?? null,
      }));
    }
    if (training4Sections > 0) {
      const training3Complete = (p3?.markedComplete === true) || (p3?.completed?.length ?? 0) >= training3Sections;
      const completed = p4?.completed?.length ?? 0;
      const isComplete = (p4?.markedComplete === true) || completed >= training4Sections;
      setTraining4Status((s) => ({
        ...s,
        isLocked: !training3Complete,
        progress: p4?.progress ?? 0,
        completedSections: completed,
        totalSections: training4Sections,
        isComplete,
        lastUpdated: p4?.lastUpdated ?? null,
      }));
    }
    if (training5Sections > 0) {
      const training4CompleteFromApi = (p4?.markedComplete === true) || (p4?.completed?.length ?? 0) >= training4Sections;
      const training4Complete = training4CompleteFromApi || isTraining5Unlocked(training4Sections, traineeId);
      const completed = p5?.completed?.length ?? 0;
      const isComplete = (p5?.markedComplete === true) || completed >= training5Sections;
      setTraining5Status((s) => ({
        ...s,
        isLocked: !training4Complete,
        progress: p5?.progress ?? 0,
        completedSections: completed,
        totalSections: training5Sections,
        isComplete,
        lastUpdated: p5?.lastUpdated ?? null,
      }));
    }
    if (training6Sections > 0) {
      const training5CompleteFromApi = (p5?.markedComplete === true) || (p5?.completed?.length ?? 0) >= training5Sections;
      const training5Complete = training5CompleteFromApi || isTraining6Unlocked(training5Sections, traineeId);
      const completed = p6?.completed?.length ?? 0;
      const isComplete = (p6?.markedComplete === true) || completed >= training6Sections;
      setTraining6Status((s) => ({
        ...s,
        isLocked: !training5Complete,
        progress: p6?.progress ?? 0,
        completedSections: completed,
        totalSections: training6Sections,
        isComplete,
        lastUpdated: p6?.lastUpdated ?? null,
      }));
    }
    if (training7Sections > 0) {
      const training6CompleteFromApi = (p6?.markedComplete === true) || (p6?.completed?.length ?? 0) >= training6Sections;
      const training6Complete = training6CompleteFromApi || isTraining7Unlocked(training6Sections, traineeId);
      const completed = p7?.completed?.length ?? 0;
      const isComplete = (p7?.markedComplete === true) || completed >= training7Sections;
      setTraining7Status((s) => ({
        ...s,
        isLocked: !training6Complete,
        progress: p7?.progress ?? 0,
        completedSections: completed,
        totalSections: training7Sections,
        isComplete,
        lastUpdated: p7?.lastUpdated ?? null,
      }));
    }
    if (training8Sections > 0) {
      const training7CompleteFromApi = (p7?.markedComplete === true) || (p7?.completed?.length ?? 0) >= training7Sections;
      const training7Complete = training7CompleteFromApi || isTraining8Unlocked(training7Sections, traineeId);
      const completed = p8?.completed?.length ?? 0;
      const isComplete = (p8?.markedComplete === true) || completed >= training8Sections;
      setTraining8Status((s) => ({
        ...s,
        isLocked: !training7Complete,
        progress: p8?.progress ?? 0,
        completedSections: completed,
        totalSections: training8Sections,
        isComplete,
        lastUpdated: p8?.lastUpdated ?? null,
      }));
    }
  };

  const fallbackToLocalStorage = () => {
    if (training1Sections > 0) {
      const status1 = getTrainingStatus(1, training1Sections, training1Sections, traineeId);
      setTraining1Status(status1);
    }
    if (training2Sections > 0) {
      const status2 = getTrainingStatus(2, training1Sections, training2Sections, traineeId);
      setTraining2Status(status2);
    }
    if (training3Sections > 0) {
      const status3 = getTrainingStatus(3, training2Sections, training3Sections, traineeId);
      setTraining3Status(status3);
    }
    if (training4Sections > 0) {
      const status4 = getTrainingStatus(4, training3Sections, training4Sections, traineeId);
      setTraining4Status(status4);
    }
    if (training5Sections > 0) {
      const status5 = getTrainingStatus(5, training4Sections, training5Sections, traineeId);
      setTraining5Status(status5);
    }
    if (training6Sections > 0) {
      const status6 = getTrainingStatus(6, training5Sections, training6Sections, traineeId);
      setTraining6Status(status6);
    }
    if (training7Sections > 0) {
      const status7 = getTrainingStatus(7, training6Sections, training7Sections, traineeId);
      setTraining7Status(status7);
    }
    if (training8Sections > 0) {
      const status8 = getTrainingStatus(8, training7Sections, training8Sections, traineeId);
      setTraining8Status(status8);
    }
  };

  // Load training statuses from API (cross-device), fall back to localStorage
  useEffect(() => {
    if (!isClient || typeof window === 'undefined') return;
    fetch('/api/training/progress', { credentials: 'same-origin' })
      .then((res) => {
        if (res.status === 503 || !res.ok) {
          fallbackToLocalStorage();
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data?.progress && Array.isArray(data.progress)) {
          applyProgressFromApi(data.progress);
        } else {
          fallbackToLocalStorage();
        }
      })
      .catch(fallbackToLocalStorage);
  }, [isClient, training1Sections, training2Sections, training3Sections, training4Sections, training5Sections, training6Sections, training7Sections, training8Sections, traineeId]);

  // Listen for training progress updates (refetch from API)
  useEffect(() => {
    if (!isClient || typeof window === 'undefined') return;
    const handleProgressUpdate = (event: any) => {
      if (event.detail?.traineeId === traineeId) {
        fetch('/api/training/progress', { credentials: 'same-origin' })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data?.progress && Array.isArray(data.progress)) {
              applyProgressFromApi(data.progress);
            } else {
              fallbackToLocalStorage();
            }
          })
          .catch(fallbackToLocalStorage);
      }
    };
    window.addEventListener('trainingProgressUpdated', handleProgressUpdate as EventListener);
    return () => window.removeEventListener('trainingProgressUpdated', handleProgressUpdate as EventListener);
  }, [isClient, training1Sections, training2Sections, training3Sections, training4Sections, training5Sections, training6Sections, training7Sections, training8Sections, traineeId]);

  return (
    <>
      <Section id="training-dashboard" background="background" padding="lg">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            title="Training Dashboard"
            description="Complete each training in order to unlock the next"
            centered
            className="mb-12"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
            {/* Training 1 Card */}
            <TrainingCard
              trainingNumber={1}
              title={training1Info.title}
              description={training1Info.description}
              progress={training1Status.progress}
              isLocked={training1Status.isLocked}
              isComplete={training1Status.isComplete}
              completedSections={training1Status.completedSections}
              totalSections={training1Status.totalSections}
              href="/training/1"
            />

            {/* Training 2 Card */}
            <TrainingCard
              trainingNumber={2}
              title={training2Info.title}
              description={training2Info.description}
              progress={training2Status.progress}
              isLocked={training2Status.isLocked}
              isComplete={training2Status.isComplete}
              completedSections={training2Status.completedSections}
              totalSections={training2Status.totalSections}
              href="/training/2"
            />

            {/* Training 3 Card (only if content is available) */}
            {training3Content.trim() && (
              <TrainingCard
                trainingNumber={3}
                title={training3Info.title}
                description={training3Info.description}
                progress={training3Status.progress}
                isLocked={training3Status.isLocked}
                isComplete={training3Status.isComplete}
                completedSections={training3Status.completedSections}
                totalSections={training3Status.totalSections}
                href="/training/3"
              />
            )}

            {/* Training 4 Card (only if content is available) */}
            {training4Content.trim() && (
              <TrainingCard
                trainingNumber={4}
                title={training4Info.title}
                description={training4Info.description}
                progress={training4Status.progress}
                isLocked={training4Status.isLocked}
                isComplete={training4Status.isComplete}
                completedSections={training4Status.completedSections}
                totalSections={training4Status.totalSections}
                href="/training/4"
              />
            )}

            {/* Training 5 Card (only if content is available) */}
            {training5Content.trim() && (
              <TrainingCard
                trainingNumber={5}
                title={training5Info.title}
                description={training5Info.description}
                progress={training5Status.progress}
                isLocked={training5Status.isLocked}
                isComplete={training5Status.isComplete}
                completedSections={training5Status.completedSections}
                totalSections={training5Status.totalSections}
                href="/training/5"
              />
            )}

            {/* Training 6 Card (only if content is available) */}
            {training6Content.trim() && (
              <TrainingCard
                trainingNumber={6}
                title={training6Info.title}
                description={training6Info.description}
                progress={training6Status.progress}
                isLocked={training6Status.isLocked}
                isComplete={training6Status.isComplete}
                completedSections={training6Status.completedSections}
                totalSections={training6Status.totalSections}
                href="/training/6"
              />
            )}

            {/* Training 7 Card (only if content is available) */}
            {training7Content.trim() && (
              <TrainingCard
                trainingNumber={7}
                title={training7Info.title}
                description={training7Info.description}
                progress={training7Status.progress}
                isLocked={training7Status.isLocked}
                isComplete={training7Status.isComplete}
                completedSections={training7Status.completedSections}
                totalSections={training7Status.totalSections}
                href="/training/7"
              />
            )}

            {/* Training 8 Card (only if content is available) */}
            {training8Content.trim() && (
              <TrainingCard
                trainingNumber={8}
                title={training8Info.title}
                description={training8Info.description}
                progress={training8Status.progress}
                isLocked={training8Status.isLocked}
                isComplete={training8Status.isComplete}
                completedSections={training8Status.completedSections}
                totalSections={training8Status.totalSections}
                href="/training/8"
              />
            )}
          </div>
        </div>
      </Section>
    </>
  );
}
